import { CommonModule } from '@angular/common'
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { ProgressBarMode } from '@angular/material/progress-bar'
import { NavigationEnd, Router, RouterModule } from '@angular/router'
import { Store } from '@ngrx/store'
import { Subject, takeUntil } from 'rxjs'
import { DdcAddDialogComponent } from 'src/app/core/ddc-add-dialog/ddc-add-dialog.component'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { dark_theme, light_theme, paper_theme } from 'src/app/ngrx/store/actions'
import { AccountService } from 'src/app/service/account/account.service'
import { AuthUserService } from 'src/app/service/auth/auth-user.service'
import { CommonInfoService } from 'src/app/service/common-info/common-info.service'
import { DdcLoggerService } from 'src/app/service/log/ddc-logger.service'
import { NetworkService } from 'src/app/service/network/network.service'
import {
  Notification,
  NotificationService,
} from 'src/app/service/notifications/notification.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { environment } from 'src/environments/environment'
import { AppState } from '../../../ngrx/states'
import { CommonInfo, Media, Stages, Themes } from '../../enums/enums'
import { DdcButtonAiComponent } from '../ddc-button-ai/ddc-button-ai.component'
import { DdcButtonNOneActionComponent } from '../ddc-button-n-one-action/ddc-button-n-one-action.component'
import { DdcButtonNOneComponent } from '../ddc-button-n-one/ddc-button-n-one.component'
import { DdcGeneralSearchDialogComponent } from '../ddc-general-search-dialog/ddc-general-search-dialog.component'
import { DdcHintsCarouselDialogComponent } from '../ddc-hints-carousel-dialog/ddc-hints-carousel-dialog.component'
import { DdcInfoCardDialogComponent } from '../ddc-info-card-dialog/ddc-info-card-dialog.component'
import { DdcLogoComponent } from '../ddc-logo/ddc-logo.component'
import { NotificationContentComponent } from './notification-content.component'

@Component({
  selector: 'ddc-navbar',
  templateUrl: './ddc-navbar.component.html',
  styleUrls: ['./ddc-navbar.component.scss'],
  imports: [
    AngularMaterialsModules,
    CommonModule,
    RouterModule,
    DdcLogoComponent,
    DdcButtonNOneComponent,
    DdcButtonNOneActionComponent,
    DdcButtonAiComponent,
    NotificationContentComponent,
  ],
  encapsulation: ViewEncapsulation.Emulated,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcNavbarComponent implements OnInit, OnDestroy {
  onNotificationMenuOpened(): void {
    this._hasNotifications.set(false)
  }

  openHintsOverlay(): void {
    // Example images, replace with your actual hint images
    const images = [
      Media.MEDIA + '/hints/1.png',
      Media.MEDIA + '/hints/2.png',
      Media.MEDIA + '/hints/3.png',
      Media.MEDIA + '/hints/4.png',
      Media.MEDIA + '/hints/5.png',
      Media.MEDIA + '/hints/6.png',
      Media.MEDIA + '/hints/7.png',
      Media.MEDIA + '/hints/8.png',
      Media.MEDIA + '/hints/9.png',
      Media.MEDIA + '/hints/10.png',
    ]
    this.dialog.open(DdcHintsCarouselDialogComponent, {
      data: { images },
      panelClass: 'custom-ddc-dialog',
      backdropClass: 'carousel-overlay',
      autoFocus: false,
      restoreFocus: true,
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
    })
  }
  onNotificationMenuClosed(): void {
    this._hasNotifications.set(false)

    // Mark all notifications as read
    this._recentNotifications = this._recentNotifications.map(notification => ({
      ...notification,
      is_read: true,
    }))

    // send request to backend to mark notifications as read
    this.notificationService.mark_all_as_read().subscribe({
      next: () => {
        this.logger.info('DdcNavbarComponent: All notifications marked as read on the backend.')
      },
      error: error => {
        this.logger.error(
          'DdcNavbarComponent: Error marking notifications as read on the backend.',
          error,
        )
      },
    })
  }
  //TODO: get username from db after login, for now admin
  _username: string
  _isAdmin: boolean = false
  _adminInfo: any
  _currentUserToken: any
  _isDarkMode: boolean
  _isVerified: boolean = false
  _subscription: string = 'Free Tier'
  _isProUser: boolean = false
  _NavbarError: string
  _hasShownWelcomeDialog: boolean = false
  private destroy$ = new Subject<void>()

  _theme: string

  _currentPage: string
  _isSignupPage: boolean = false
  _isLoginPage: boolean = false
  _isVerificationPage: boolean = false
  _isStorePage: boolean = false
  _isLibraryPage: boolean = false
  _isCanvasPage: boolean = false
  _isNetworkPage: boolean = false
  _isPlayPage: boolean = false
  _isAgentsPage: boolean = false

  _progressBarMode: ProgressBarMode = 'indeterminate'

  _clickedColorLibrary: string = 'basic'
  _clickedColorCanvas: string = 'basic'
  _clickedColorSearch: string = 'basic'
  _clickedColorStore: string = 'basic'
  _clickedColorNetwork: string = 'basic'
  _clickedColorPlay: string = 'basic'
  _clickedColorAgents: string = 'basic'

  _userProfilePic: string
  _DefaultProfilePic: string =
    'https://st3.depositphotos.com/6672868/13701/v/600/depositphotos_137014128-stock-illustration-user-profile-icon.jpg'
  _isProfilePicLoading: boolean = true
  _hasPic: boolean = false
  _isLoggedIn: boolean = false
  _stages: number[] = []

  _createLabel: string = 'Create'
  _searchLabel: string = 'Search | ⌘K'

  // Notification properties
  _hasNotifications = signal<boolean>(false)
  _recentNotifications: any[] = []
  _isNotificationDropdownOpen: boolean = false
  private notificationSub: any = null

  protected unreadNotificationsCount = computed(
    () => this._recentNotifications.filter(n => !n.is_read).length,
  )
  // User WebSocket connection status (only /user, not general)
  _isWebSocketConnected: boolean = false

  @HostListener('window:resize', ['$event'])
  // onResize(event: Event): void {
  //   this._searchLabel = window.innerWidth < 670 ? '⌘K' : 'Search | ⌘K'
  //   this._createLabel = window.innerWidth < 670 ? 'Add' : 'Create'
  // }
  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
    if (
      (isMac && event.metaKey && event.key === 'k') ||
      (!isMac && event.ctrlKey && event.key === 'k')
    ) {
      event.preventDefault()
      this.openSearchDialog()
    }
  }

  constructor(
    private logger: DdcLoggerService,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private auth_user: AuthUserService,
    private user_info: UserService,
    private common_info: CommonInfoService,
    private router: Router,
    public themeService: ThemeService,
    private notificationService: NotificationService,
  ) {
    // reload
    const reloadCurrentUrl = window.location.pathname
    this.executeSelectPage(reloadCurrentUrl)
    // select navbar button
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.urlAfterRedirects
        this.executeSelectPage(currentUrl)
      }
    })
  }

  ngOnInit(): void {
    this.auth_user
      .is_logged_in()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res.has_valid_tokens) {
          this.getUserNavbarInfo()
        } else if (res.detail == 'Token has expired') {
          this.auth_user
            .refresh_access_token()
            .pipe(takeUntil(this.destroy$))
            .subscribe((res: any) => {
              if (res.message != 'Access token sent as cookie') {
                this.router.navigate(['/login'])
              }
              this.auth_user
                .is_logged_in()
                .pipe(takeUntil(this.destroy$))
                .subscribe((res: any) => {
                  if (res.has_valid_tokens) {
                    this.getUserNavbarInfo()
                  } else {
                    this.router.navigate(['/login'])
                  }
                })
            })
        } else {
          this.router.navigate(['/login'])
        }
      })
    //set theme
    this.initTheme()

    // fetch the  recent 10 notifications (only after confirming auth in getUserNavbarInfo)
    // moved inside getUserNavbarInfo() to avoid 403 on unauthenticated routes
  }

  private fetchRecentNotifications(): void {
    this.notificationService
      .get_notifications_page(1)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res && res.results) {
          this._recentNotifications = res.results.map((notification: Notification) => ({
            id: notification.id,
            type: notification.type,
            message: notification.message,
            actor: notification.actor,
            shared_user: notification.shared_user,
            content_title: notification.content_title,
            content_type: notification.content_type,
            object_id: notification.object_id,
            created_at: notification.created_at,
            is_read: notification.is_read,
            time_since: this.getTimeSince(notification.created_at),
          }))
          this._hasNotifications.set(this._recentNotifications.some((n: any) => !n.is_read))
        }
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    if (this.notificationSub) {
      this.notificationSub.unsubscribe()
    }
  }

  executeSelectPage(currentUrl: string): void {
    this.logger.info('DdcNavbarComponent executeSelectPage() currentUrl:', currentUrl)

    if (currentUrl.startsWith('/store')) {
      this.selectPage('store')
    } else if (currentUrl.startsWith('/library')) {
      this.selectPage('library')
    } else if (currentUrl.startsWith('/canvas')) {
      this.selectPage('canvas')
    } else if (currentUrl.startsWith('/play')) {
      this.selectPage('play')
    } else if (currentUrl.startsWith('/network')) {
      this.selectPage('network')
    } else if (currentUrl.startsWith('/agents')) {
      this.selectPage('agents')
    } else {
      this.selectPage('')
    }
  }

  selectPage(page: string): void {
    this.logger.info('DdcNavbarComponent selectPage() page:', page)
    //reset state
    this._isStorePage = false
    this._isLibraryPage = false
    this._isCanvasPage = false
    this._isNetworkPage = false
    this._isPlayPage = false
    this._isAgentsPage = false
    if (page === 'store') this._isStorePage = true
    else if (page === 'library') this._isLibraryPage = true
    else if (page === 'canvas') this._isCanvasPage = true
    else if (page === 'agents') this._isAgentsPage = true
    else if (page === 'network') this._isNetworkPage = true
    else if (page === 'play') this._isPlayPage = true
  }

  getUserNavbarInfo(): void {
    this.logger.info('DdcNavbarComponent getUserNavbarInfo()')
    // get user info once
    this.user_info.get_user_info()
    this._isProfilePicLoading = true

    this.user_info.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res && res.username) {
        this._username = res.username
        this._isAdmin = res.is_admin

        if (environment.maintenance && !this._isAdmin) {
          this.router.navigate(['/maintenance'])
        }

        this._isVerified = res.is_verified
        this._userProfilePic = res.profile_pic
        this._stages = res.stages
        this._progressBarMode = 'determinate'
        this._subscription = res.subscription
        if (this._stages.includes(2) && !this._stages.includes(3) && !this._hasShownWelcomeDialog) {
          this._hasShownWelcomeDialog = true
          this.openInfoCardDialog()
        }

        if (this._userProfilePic) {
          this._hasPic = true
          this._isProfilePicLoading = false
          this._userProfilePic = Media.ASSETS + this._userProfilePic
        }

        // Fetch notifications only after confirming auth
        this.fetchRecentNotifications()
      } else if (res) {
        this.router.navigate(['/login'])
      }
    })
  }

  openAddDialog(): void {
    this.logger.info('DdcNavbarComponent openAddDialog()')

    const dialogRef = this.dialog.open(DdcAddDialogComponent, {
      data: {
        hasDic: false,
        dicId: 0,
        dicTitle: '',
        targetChip: 'word',
      },
      // panelClass: 'custom-ddc-dialog'
    })

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        // Handle any actions after the dialog is closed, e.g., updating the user's profile picture.
        if (result === 'dialogClosed') {
          // Reload or refresh the parent page
          window.location.reload()
        }
      })
  }

  openInfoCardDialog(): void {
    this.logger.info('DdcNavbarComponent openInfoCardDialog()')

    this.common_info.get_common_info(CommonInfo.WELCOME).subscribe((res: any) => {
      if (res.length == 3) {
        // provide steps data and the finished stage
        const dialogRef = this.dialog.open(DdcInfoCardDialogComponent, {
          data: {
            stepData: res,
            finishedStep: Stages.SEEN_WELCOME,
          },
          panelClass: 'custom-ddc-dialog',
        })

        dialogRef
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe(result => {
            // Handle any actions after the dialog is closed, e.g., updating the user's profile picture.
            if (result === 'dialogClosed') {
              // Reload or refresh the parent page
              window.location.reload()
            }
          })
      }
    })
  }

  initTheme(): void {
    this.logger.info('DdcNavbarComponent initTheme()')

    this._theme = this.themeService.currentTheme
    this.onThemeChipChange(this._theme)
  }

  //TODO fix error NG0100:ExpressionChangedAfterItHasBeenCheckedError
  onThemeChipChange(value: string): void {
    this.logger.info('DdcNavbarComponent onThemeChipChange() value:', value)

    this._theme = value

    if (value === Themes.DARK) {
      this.store.dispatch(dark_theme())
      this.themeService.set(Themes.DARK)
    } else if (value === Themes.LIGHT) {
      this.store.dispatch(light_theme())
      this.themeService.set(Themes.LIGHT)
    } else if (value === Themes.PAPER) {
      this.store.dispatch(paper_theme())
      this.themeService.set(Themes.PAPER)
    } else {
      this.store.dispatch(light_theme())
      this.themeService.set(Themes.LIGHT)
    }
  }

  makeTabButtonPrimary(element: string): void {
    this.logger.info('DdcNavbarComponent makeTabButtonPrimary() element:', element)

    this._clickedColorStore = 'basic'
    this._clickedColorLibrary = 'basic'
    this._clickedColorCanvas = 'basic'
    this._clickedColorNetwork = 'basic'
    this._clickedColorSearch = 'basic'
    this._clickedColorPlay = 'basic'
    this._clickedColorAgents = 'basic'

    if (element == 'none' || element == 'home') {
      // do nothing
    } else if (element == 'library') this._clickedColorLibrary = 'primary'
    else if (element == 'canvas') this._clickedColorCanvas = 'primary'
    else if (element == 'agents') this._clickedColorAgents = 'primary'
    else if (element == 'network') this._clickedColorNetwork = 'primary'
    else if (element == 'play') this._clickedColorPlay = 'primary'
    else if (element == 'store') this._clickedColorStore = 'primary'
    else if (element == 'admin') {
      // do nothing
    }
  }

  openSearchDialog(): void {
    this.logger.info('DdcNavbarComponent openSearchDialog()')

    this.dialog.open(DdcGeneralSearchDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '80vh',
      position: {
        top: '6.25vh', // 1/8 from top
      },
      panelClass: 'search-dialog-container',
      autoFocus: true,
      restoreFocus: true,
    })
  }

  openLogOutDialog(): void {
    this.logger.info('DdcNavbarComponent openLogOutDialog()')

    this.dialog.open(DialogLogout)
  }

  getNotificationIcon(type: string): string {
    switch (type) {
      case 'like':
        return 'favorite'
      case 'follow':
        return 'person_add'
      case 'comment':
        return 'comment'
      case 'share':
        return 'share'
      case 'system':
        return 'info'
      default:
        return 'notifications'
    }
  }

  getNotificationMessage(notification: Notification): string {
    return notification.message || ''
  }

  getTimeSince(dateString: string): string {
    // Simple time since logic
    const now = new Date()
    const date = new Date(dateString)
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours} hr ago`
    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays} d ago`
  }
}

@Component({
  selector: 'logout-dialog',
  templateUrl: '../../dialogs/logout-dialog.html',
  imports: [AngularMaterialsModules, CommonModule, RouterModule],
})
export class DialogLogout {
  constructor(
    private logger: DdcLoggerService,
    public dialogRef: MatDialogRef<DialogLogout>,
    public auth_user: AuthUserService,
    private router: Router,
  ) {}

  clickedOK(): void {
    this.logger.info('DialogLogout clickedOK()')

    this.auth_user.sign_out().subscribe((res: any) => {
      if (res.message && res.message == 'Logged out successfully') {
        this.router.navigate(['/login'])
        this.dialogRef.close()
      }
    })
  }
}

@Component({
  selector: 'are-you-sure-dialog',
  templateUrl: '../../dialogs/are-you-sure-dialog.html',
  imports: [AngularMaterialsModules, CommonModule, RouterModule],
})
export class DialogAreYouSure {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private logger: DdcLoggerService,
    public dialogRef: MatDialogRef<DialogAreYouSure>,
    private network_srv: NetworkService,
    private account_srv: AccountService,
  ) {}

  clickedOK(): void {
    this.logger.info('DialogAreYouSure clickedOK()')
    // ! remove user
    if (this.data.type === 'remove_user') {
      this.network_srv.command(this.data.username, 'remove').subscribe((res: any) => {})
      this.dialogRef.close('removed')
    }
    // ! unfollow user
    else if (this.data.type === 'unfollow_user') {
      this.network_srv.command(this.data.username, 'unfollow').subscribe((res: any) => {})
      this.dialogRef.close('unfollowed')
    }
    // ! reset data
    else if (this.data.type === 'reset_data') {
      this.account_srv.settings('reset_data').subscribe((res: any) => {})
      this.dialogRef.close('reset_data')
    }
    // ! reset network
    else if (this.data.type === 'reset_network') {
      this.account_srv.settings('reset_network').subscribe((res: any) => {})
      this.dialogRef.close('reset_network')
    }
    // ! delete account
    else if (this.data.type === 'delete_account') {
      this.account_srv.settings('delete_account').subscribe((res: any) => {})
      this.dialogRef.close('delete_account')
    }
  }
}
